import { skipToken } from '@reduxjs/toolkit/query'
import { useGetAllTagsByClientAuthorizedQuery } from 'core/api/tag'
import Loader from 'core/components/common/Loader'
import { ROUTE_PATHS } from 'core/constants/routePath'
import { UserContext } from 'core/contexts/UserContext'
import {
  IKeycloakUtilisateur,
  IKeycloakUtilisateurColumn,
} from 'core/interfaces/User'
import { useGetAllKeycloakUsersQuery } from 'lauria/api/keycloakUtilisateurs'
import { ProfilesList } from 'lauria/components/profiles/ProfilesList'
import { ProfilesListMobile } from 'lauria/components/profiles/ProfilesListMobile'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

const PAGE_SIZE = 20

export const Profiles = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [searchTerm, setSearchTerm] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [page, setPage] = useState<number>(1)

  const {
    user: currentUser,
    isAdmin: isCurrentUserAdmin,
    isSuperAdmin: isCurrentUserSuperAdmin,
  } = useContext(UserContext)

  const idClient = currentUser?.idClient
  const { data: tags, isLoading: isLoadingTags } =
    useGetAllTagsByClientAuthorizedQuery(idClient ? { idClient } : skipToken)

  const {
    data: usersCollection,
    isLoading,
    isFetching,
  } = useGetAllKeycloakUsersQuery(
    idClient ? { idClient, search: searchQuery, page } : skipToken
  )

  const [profiles, setProfiles] = React.useState<IKeycloakUtilisateurColumn[]>(
    []
  )

  useEffect(() => {
    if (tags && usersCollection) {
      const tagsMap = new Map<number, string>(
        tags.map((tag) => [tag.id, tag.nomTag])
      )

      setProfiles(
        usersCollection.users.map((user: IKeycloakUtilisateur) => ({
          id: user.id,
          email: user.email,
          nom: user.lastName,
          prenom: user.firstName,
          tagsUtilisateur: (user?.tagsUtilisateur ?? [])
            .map((tagId) => tagsMap.get(tagId) || '')
            .filter((tag) => tag !== '')
            .sort(),
          tagsAdministrateur: (user?.tagsAdministrateur ?? [])
            .map((tagId) => tagsMap.get(tagId) || '')
            .filter((tag) => tag !== '')
            .sort(),
        }))
      )
    }
  }, [tags, usersCollection])

  if (!isCurrentUserAdmin && !isCurrentUserSuperAdmin) {
    navigate(ROUTE_PATHS.DASHBOARD)
  }

  if (isLoading || isLoadingTags) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Loader pageCenter />
      </div>
    )
  }

  const handleSearch = () => {
    // on revient en page 1 quand on change la recherche
    setPage(1)
    setSearchQuery(searchTerm)
  }

  return (
    <>
      <div className="profiles-list-wrapper md:flex hidden relative">
        {isFetching && !isLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
            <Loader />
          </div>
        )}
        <div className="profile-search">
          <input
            type="search"
            placeholder={t('profiles.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border border-gray-300 p-2 rounded-[5px] w-[400px] h-[50px] text-xl mr-[20px]"
          />

          <button
            onClick={handleSearch}
            className="btn default"
            disabled={isLoading || isFetching}
          >
            {t('profiles.searchButton')}
            {(isLoading || isFetching) && (
              <span className="button-loader ml-2 align-middle"></span>
            )}
          </button>
        </div>

        <ProfilesList
          users={profiles}
          total={usersCollection?.total ?? 0}
          currentPage={page}
          pageSize={PAGE_SIZE}
          onChangePage={setPage}
        />
      </div>
      <div className="profiles-list-wrapper flex md:hidden relative">
        {isFetching && !isLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
            <Loader />
          </div>
        )}
        <ProfilesListMobile users={profiles} />
      </div>
    </>
  )
}
